import type confetti from "canvas-confetti";
import type { CelebrationOptions, LegacyCelebrationIntensity } from "./celebration.types";

export const MINIMAL_CELEBRATION_DURATION_MS = 5_000;
const CELEBRATION_COLORS = ["#F8FAFC", "#FACC15", "#22C55E", "#2563EB", "#111827"];

export function shouldBypassCelebrationCooldown(
  type: CelebrationOptions["type"],
) {
  return type === "achievement-unlocked" || type === "fireworks";
}

export function getCelebrationDurationMs(options: CelebrationOptions) {
  if (options.type === "block-generated") return MINIMAL_CELEBRATION_DURATION_MS;
  if (options.type === "achievement-unlocked") return 0;
  if (options.type !== "fireworks") return 0;

  if (options.intensity === "high") return 5_000;
  if (options.intensity === "med") return 4_000;
  return 3_000;
}

export function getPerformanceScale() {
  if (typeof navigator === "undefined") return 1;
  const hardwareThreads = navigator.hardwareConcurrency ?? 8;
  const memory =
    (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 8;
  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(
    navigator.userAgent,
  );
  if (isMobile || hardwareThreads <= 4 || memory <= 4) return 0.6;
  return 1;
}

function intensityScale(intensity: LegacyCelebrationIntensity) {
  if (intensity === "low") return 0.75;
  if (intensity === "high") return 1.2;
  return 1;
}

export function fireBlockGeneratedConfetti(
  fire: confetti.CreateTypes,
  perfScale: number,
) {
  fire({
    particleCount: Math.max(16, Math.round(28 * perfScale)),
    spread: 46,
    startVelocity: 28,
    decay: 0.92,
    gravity: 1.08,
    ticks: 58,
    origin: { x: 0.5, y: 0.54 },
    colors: CELEBRATION_COLORS,
    shapes: ["circle", "square"],
    scalar: 0.78,
  });
}

export function fireAchievementConfetti(
  fire: confetti.CreateTypes,
  perfScale: number,
) {
  const particleCount = Math.max(38, Math.round(72 * perfScale));
  fire({
    particleCount,
    spread: 72,
    startVelocity: 38,
    decay: 0.92,
    gravity: 0.96,
    ticks: 130,
    origin: { x: 0.5, y: 0.42 },
    colors: CELEBRATION_COLORS,
    shapes: ["star", "circle"],
    scalar: 0.95,
  });

  window.setTimeout(() => {
    fire({
      particleCount: Math.max(18, Math.round(particleCount * 0.42)),
      spread: 58,
      startVelocity: 32,
      decay: 0.93,
      gravity: 1,
      ticks: 100,
      origin: { x: 0.5, y: 0.5 },
      colors: CELEBRATION_COLORS,
      shapes: ["circle", "square"],
      scalar: 0.82,
    });
  }, 180);
}

export function fireLegacyBurst(
  fire: confetti.CreateTypes,
  intensity: LegacyCelebrationIntensity,
  perfScale: number,
) {
  const particleScale = perfScale * intensityScale(intensity);
  fire({
    particleCount: Math.max(24, Math.round(46 * particleScale)),
    spread: 78,
    startVelocity: 34,
    decay: 0.91,
    gravity: 1.1,
    ticks: 70,
    origin: { x: 0.5, y: 0.62 },
    shapes: ["star"],
    colors: CELEBRATION_COLORS,
  });
}
